import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Div, Text } from "atomize"
import { motion, useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGripVertical, faLock } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { findCard, moveCard } from "./profileLayout"

// ──────────────────────────────────────────────────────────────────────────
// IL RIORDINO DEL PROFILO
//
// Le schede si chiudono e resta il titolo: righe basse, che stanno tutte
// sullo schermo e si spostano col dito. Quella presa si solleva e segue il
// dito; dove andrebbe a cadere resta un posto tratteggiato, e le altre si
// fanno da parte. Sul PC le colonne sono due, e una scheda passa dall'una
// all'altra. Ogni spostamento si salva subito: "Fatto" chiude e basta.
//
// Non è framer `Reorder`: quello sposta dentro un elenco solo, e qui le
// colonne sono due. I posti si contano sull'impaginazione (`offsetTop`), non
// su quello che si vede, che a metà animazione è ancora per strada.
// ──────────────────────────────────────────────────────────────────────────

const ROW_HEIGHT = 60
// Entrando le righe partono alte come le schede e si chiudono, e il titolo
// parte grande come quello della scheda. Uscendo sono le schede a riaprirsi
// dalle righe, col loro contenuto (ProfileSlot).
const MORPH = { duration: 0.38, ease: [0.04, 0.62, 0.23, 0.98] }
const CARD_TITLE_SCALE = 1.25
// vicino ai bordi dello schermo la pagina scorre da sola, per chi ha più
// schede di quante ne stanno
const EDGE = 80
const SCROLL_STEP = 10

const scrollParent = element => {
	for (let node = element?.parentElement; node; node = node.parentElement) {
		const { overflowY } = window.getComputedStyle(node)
		if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node
	}
	return document.scrollingElement
}

const Bubble = ({ card }) => (
	<Div
		w="36px"
		h="36px"
		rounded="circle"
		bg={card.bg}
		d="flex"
		align="center"
		justify="center"
		style={{ flexShrink: 0 }}>
		<FontAwesomeIcon icon={card.icon} color={card.color} size="sm" />
	</Div>
)

// Il contenuto sta in alto, non in mezzo: mentre la riga è ancora alta
// come la scheda, il titolo sta dove stava quello della scheda
const ROW_PAD = (ROW_HEIGHT - 36) / 2
const rowStyle = {
	boxSizing: "border-box",
	padding: `${ROW_PAD}px 0.9rem 0 0.85rem`,
	display: "flex",
	alignItems: "flex-start",
	gap: "0.75rem",
	borderRadius: "18px",
	overflow: "hidden",
	background: theme.colors.surface,
}

// `from`: la riga parte con l'aspetto della scheda (titolo grande, niente
// puntini). Si prende dai puntini, e solo da lì (`onGrab`): il
// resto della riga lascia scorrere la pagina, così un dito che scorre non
// sposta niente per sbaglio.
const RowContent = ({ card, lifted, from = false, onGrab }) => (
	<>
		<Bubble card={card} />
		<motion.div
			initial={from ? { scale: CARD_TITLE_SCALE } : false}
			animate={{ scale: 1 }}
			transition={MORPH}
			style={{ flexGrow: 1, minWidth: 0, transformOrigin: "left center" }}>
			<Text
				textWeight="600"
				textSize="subheader"
				textColor={theme.colors.gunmetal}
				style={{
					lineHeight: "36px",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}>
				{card.title}
			</Text>
		</motion.div>
		<motion.span
			initial={from ? { opacity: 0 } : false}
			animate={{ opacity: 1 }}
			transition={MORPH}
			data-drag-handle
			onPointerDown={onGrab}
			// più largo dei puntini, per il dito, senza spostarli
			style={{
				display: "flex",
				width: "40px",
				height: "44px",
				margin: "-4px -12px 0 -8px",
				alignItems: "center",
				justifyContent: "center",
				cursor: lifted ? "grabbing" : "grab",
				touchAction: "none",
			}}>
			<FontAwesomeIcon
				icon={faGripVertical}
				color={lifted ? theme.colors.gunmetal : theme.colors.text_light}
			/>
		</motion.span>
	</>
)

const LockedRow = ({ title, note }) => (
	<Div
		d="flex"
		align="center"
		h="48px"
		p={{ x: "1rem" }}
		rounded="18px"
		bg={theme.colors.surface}
		style={{ gap: "0.6rem", opacity: 0.55 }}>
		<FontAwesomeIcon icon={faLock} color={theme.colors.text_light} size="sm" />
		<Text textWeight="600" textColor={theme.colors.gunmetal}>
			{title}
		</Text>
		<Text textSize="caption" textColor={theme.colors.text_light}>
			{note}
		</Text>
	</Div>
)

// `stacked`: le colonne del PC una sotto l'altra, su uno schermo dove il
// profilo le mostra in una sola (prima la sinistra, poi la destra): il
// riordino fa vedere lo stesso ordine che poi si vede
function ProfileReorder({
	layout,
	cards,
	onChange,
	onReset,
	canReset,
	onDone,
	hasFirstSteps,
	stacked = false,
	cardHeights = null,
}) {
	const isPC = layout.length > 1
	const rootRef = useRef(null)
	const columnRefs = useRef([])
	const rowRefs = useRef({})
	const layoutRef = useRef(layout)
	layoutRef.current = layout

	// la scheda presa: dove si è presa, e dov'è il dito
	const [drag, setDrag] = useState(null)
	const dragRef = useRef(null)
	dragRef.current = drag
	// chi sposta con le frecce non deve perdere la riga dopo ogni passo
	const [focusedID, setFocusedID] = useState(null)

	// chi chiede meno movimento entra ed esce senza animazione
	const reduceMotion = useReducedMotion()
	const morphs = Boolean(cardHeights) && !reduceMotion
	const done = () => onDone()
	const cardHeight = id => cardHeights?.[id] ?? ROW_HEIGHT

	// Esc e il gesto indietro escono come "Fatto". Per il gesto indietro il
	// riordino ha una voce sua nella cronologia, con lo stesso indirizzo:
	// tornando indietro si esce dal riordino e si resta sul profilo.
	const doneRef = useRef(done)
	doneRef.current = done
	useEffect(() => {
		const onKey = event => {
			if (event.key === "Escape" && !dragRef.current) doneRef.current()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [])
	useEffect(() => {
		let popped = false
		window.history.pushState({ ...window.history.state, profileReorder: true }, "")
		const onPop = () => {
			popped = true
			doneRef.current()
		}
		window.addEventListener("popstate", onPop)
		return () => {
			window.removeEventListener("popstate", onPop)
			// usciti da "Fatto" o da Esc la voce si toglie, se no il primo
			// indietro dopo non farebbe niente; se si è andati su un'altra
			// pagina la voce in cima è già quella nuova, e resta
			if (!popped && window.history.state?.profileReorder) window.history.back()
		}
	}, [])
	// solo all'entrata: una riga che sul PC passa di colonna si rifà da capo,
	// e non deve ripartire alta come una scheda
	const [entering, setEntering] = useState(morphs)
	useEffect(() => {
		if (!entering) return
		const timer = setTimeout(() => setEntering(false), MORPH.duration * 1000 + 50)
		return () => clearTimeout(timer)
	}, [entering])

	// Si entra dall'alto della pagina, anche se la scheda premuta era in
	// fondo. In cima alla pagina, non "fino a questo elemento": sul telefono
	// con più circoli c'è la fascia arancione fissa, e il titolo con "Fatto"
	// ci finirebbe sotto. La pagina ha già il posto della fascia.
	useEffect(() => {
		scrollParent(rootRef.current)?.scrollTo({ top: 0 })
		window.scrollTo({ top: 0 })
	}, [])

	useLayoutEffect(() => {
		if (focusedID) rowRefs.current[focusedID]?.focus({ preventScroll: true })
	}, [layout, focusedID])

	// Dove cadrebbe la scheda col dito qui: la colonna sotto il dito (o la
	// più vicina, affiancata o di sopra e di sotto che sia), e in quella il posto dopo le righe che hanno il centro
	// sopra il centro della riga sollevata.
	const dropTarget = useCallback((x, y) => {
		const { id, dy } = dragRef.current
		const columns = columnRefs.current.slice(0, layoutRef.current.length)
		let column = 0
		let best = Infinity
		columns.forEach((element, c) => {
			const rect = element.getBoundingClientRect()
			const outside = (value, from, to) => (value < from ? from - value : value > to ? value - to : 0)
			const distance = Math.hypot(outside(x, rect.left, rect.right), outside(y, rect.top, rect.bottom))
			if (distance < best) {
				best = distance
				column = c
			}
		})
		const columnTop = columns[column].getBoundingClientRect().top
		const center = y - dy + ROW_HEIGHT / 2 - columnTop
		const index = layoutRef.current[column]
			.filter(other => other !== id)
			.filter(other => {
				const row = rowRefs.current[other]
				return row && row.offsetTop + row.offsetHeight / 2 < center
			}).length
		return { column, index }
	}, [])

	const follow = useCallback(
		(x, y) => {
			const current = dragRef.current
			if (!current) return
			const target = dropTarget(x, y)
			const now = findCard(layoutRef.current, current.id)
			if (target.column !== now.column || target.index !== now.index)
				onChange(moveCard(layoutRef.current, current.id, target.column, target.index))
		},
		[dropTarget, onChange],
	)

	useEffect(() => {
		if (!drag?.id) return
		const scroller = scrollParent(rootRef.current)
		const onMove = event => {
			event.preventDefault()
			setDrag(prev => prev && { ...prev, x: event.clientX, y: event.clientY })
			follow(event.clientX, event.clientY)
		}
		const onUp = () => setDrag(null)
		const tick = setInterval(() => {
			const current = dragRef.current
			if (!current) return
			const bottom = window.innerHeight - EDGE - (isPC ? 0 : ROW_HEIGHT)
			const step = current.y < EDGE ? -SCROLL_STEP : current.y > bottom ? SCROLL_STEP : 0
			if (!step) return
			scroller.scrollBy(0, step)
			follow(current.x, current.y)
		}, 16)
		window.addEventListener("pointermove", onMove, { passive: false })
		window.addEventListener("pointerup", onUp)
		window.addEventListener("pointercancel", onUp)
		return () => {
			clearInterval(tick)
			window.removeEventListener("pointermove", onMove)
			window.removeEventListener("pointerup", onUp)
			window.removeEventListener("pointercancel", onUp)
		}
	}, [drag?.id, follow, isPC])

	const startDrag = (event, id) => {
		if (event.button !== 0) return
		event.preventDefault()
		const rect = rowRefs.current[id].getBoundingClientRect()
		setFocusedID(null)
		setDrag({
			id,
			x: event.clientX,
			y: event.clientY,
			dx: event.clientX - rect.left,
			dy: event.clientY - rect.top,
			width: rect.width,
		})
	}

	const onKeyDown = (event, id) => {
		const { column, index } = findCard(layout, id)
		const moves = {
			ArrowUp: [column, index - 1],
			ArrowDown: [column, index + 1],
			ArrowLeft: [column - 1, index],
			ArrowRight: [column + 1, index],
		}
		const move = moves[event.key]
		if (!move) return
		event.preventDefault()
		let [toColumn, toIndex] = move
		// una sotto l'altra, giù dalla fine della prima si entra nella seconda
		if (stacked && toIndex > layout[column].length - 1 && column + 1 < layout.length)
			[toColumn, toIndex] = [column + 1, 0]
		if (stacked && toIndex < 0 && column > 0)
			[toColumn, toIndex] = [column - 1, layout[column - 1].length]
		if (toColumn < 0 || toColumn >= layout.length || toIndex < 0) return
		setFocusedID(id)
		onChange(moveCard(layout, id, toColumn, toIndex))
	}

	const keysHint = isPC ? "le frecce la spostano, anche di colonna" : "le frecce la spostano"

	return (
		<Div ref={rootRef} m={{ x: "1rem", t: "1rem" }}>
			<motion.div
				initial={morphs ? { opacity: 0, y: 8 } : false}
				animate={{ opacity: 1, y: 0 }}
				transition={MORPH}
				style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
				<Div flexGrow="1">
					<Text tag="h1" textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
						Riordina il profilo
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{!isPC
							? "Trascinale dai puntini, dove ti servono"
							: stacked
								? "Trascinale dai puntini; su uno schermo largo le due liste stanno affiancate"
								: "Trascinale dai puntini, anche da una colonna all'altra"}
					</Text>
				</Div>
				{isPC && canReset && (
					<Text
						tag="button"
						type="button"
						onClick={onReset}
						bg="transparent"
						textSize="body"
						textColor={theme.colors.text_light}
						cursor="pointer"
						p={{ x: "0.75rem", y: "0.6rem" }}
						style={{ border: 0, fontFamily: "inherit", flexShrink: 0 }}>
						Torna all'ordine di partenza
					</Text>
				)}
				<motion.button
					type="button"
					whileTap={{ scale: 0.94 }}
					onClick={done}
					style={{
						flexShrink: 0,
						border: 0,
						cursor: "pointer",
						background: theme.colors.gunmetal_fill,
						color: "white",
						fontFamily: "inherit",
						fontSize: "0.95rem",
						fontWeight: 600,
						padding: "0.7rem 1.4rem",
						borderRadius: "20px",
					}}>
					Fatto
				</motion.button>
			</motion.div>

			{isPC && hasFirstSteps && (
				<Div m={{ b: "0.75rem" }}>
					<LockedRow title="Primi passi" note="resta in cima" />
				</Div>
			)}

			<Div
				d="grid"
				style={{
					gridTemplateColumns: `repeat(${stacked ? 1 : layout.length}, minmax(0, 1fr))`,
					gap: "1rem",
					alignItems: "start",
				}}>
				{layout.map((ids, c) => (
					<div
						key={c}
						ref={element => (columnRefs.current[c] = element)}
						role="list"
						aria-label={isPC ? (c === 0 ? "A sinistra" : "A destra") : "Le schede del profilo"}
						style={{
							position: "relative",
							display: "flex",
							flexDirection: "column",
							gap: "0.6rem",
							minHeight: `${ROW_HEIGHT}px`,
						}}>
						{isPC && (
							<Text
								textSize="tiny"
								textWeight="600"
								textColor={theme.colors.text_light}
								p={{ x: "0.25rem" }}
								style={{ letterSpacing: "0.06em", textTransform: "uppercase" }}>
								{c === 0 ? "A sinistra" : "A destra"}
							</Text>
						)}
						{ids.map(id => {
							const card = cards[id]
							const isHeld = drag?.id === id
							return (
								<motion.div
									key={id}
									layout="position"
									initial={entering ? { height: cardHeight(id), borderRadius: 20 } : false}
									animate={{
										height: ROW_HEIGHT,
										borderRadius: 18,
									}}
									transition={{
										layout: { type: "spring", stiffness: 600, damping: 45 },
										height: MORPH,
										borderRadius: MORPH,
									}}
									ref={element => (rowRefs.current[id] = element)}
									role="listitem"
									tabIndex={0}
									aria-label={`${card.title}: ${keysHint}`}
									onKeyDown={event => onKeyDown(event, id)}
									onBlur={() => setFocusedID(prev => (prev === id ? null : prev))}
									style={{
										...rowStyle,
										userSelect: "none",
										WebkitUserSelect: "none",
										boxShadow: isHeld ? "none" : theme.shadows.accent,
										...(isHeld && {
											position: "relative",
											background: theme.colors.primary_light,
											opacity: 0.6,
										}),
									}}>
									{isHeld ? (
										<svg
											aria-hidden="true"
											style={{
												position: "absolute",
												inset: 1,
												width: "calc(100% - 2px)",
												height: "calc(100% - 2px)",
												overflow: "visible",
											}}>
											<rect
												width="100%"
												height="100%"
												rx="17"
												fill="none"
												stroke={theme.colors.primary}
												strokeWidth="2"
												strokeDasharray="9 7"
											/>
										</svg>
									) : (
										<RowContent
											card={card}
											from={entering}
											onGrab={event => startDrag(event, id)}
										/>
									)}
								</motion.div>
							)
						})}
					</div>
				))}
			</Div>

			{isPC && (
				<Div
					d="grid"
					m={{ t: "1rem" }}
					style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.75rem" }}>
					<LockedRow title="Guida" note="in fondo" />
					<LockedRow title="I tuoi dati" note="in fondo" />
					<LockedRow title="inCampo" note="in fondo" />
				</Div>
			)}

			{!isPC && canReset && (
				<Div d="flex" justify="center" m={{ t: "1rem" }}>
					<Text
						tag="button"
						type="button"
						onClick={onReset}
						bg="transparent"
						textSize="body"
						textColor={theme.colors.text_light}
						cursor="pointer"
						p="0.75rem"
						style={{ border: 0, fontFamily: "inherit" }}>
						Torna all'ordine di partenza
					</Text>
				</Div>
			)}

			{drag &&
				createPortal(
					<div
						aria-hidden="true"
						style={{
							...rowStyle,
							position: "fixed",
							height: `${ROW_HEIGHT}px`,
							left: drag.x - drag.dx,
							top: drag.y - drag.dy,
							width: drag.width,
							zIndex: 1000,
							transform: "rotate(-1.2deg) scale(1.03)",
							boxShadow: theme.shadows.lift,
							pointerEvents: "none",
							cursor: "grabbing",
						}}>
						<RowContent card={cards[drag.id]} lifted />
					</div>,
					document.body,
				)}
		</Div>
	)
}

export default ProfileReorder
